import React, { useState, useEffect, useRef } from 'react';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import ProductPlaceholder from './ProductPlaceholder';
import './ProductCarousel.scss';

const ProductCarousel = ({ products = [], onProductClick }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const scrollContainerRef = useRef(null);

  // Número de productos a mostrar a la vez
  const productsPerView = 4;
  const totalSlides = Math.ceil(products.length / productsPerView);

  // Función para obtener la URL de la imagen del producto
  const getProductImageUrl = (product) => {
    // Si existe el array de imágenes del nuevo sistema
    if (product.images && product.images.length > 0) {
      // Buscar la imagen principal o tomar la primera
      const primaryImage = product.images.find(img => img.is_primary) || product.images[0];

      // Construir URL base sin /api/v1
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
      const baseUrl = apiUrl.replace(/\/api\/v1\/?$/, '');

      // Usar medium_url para el preview
      return `${baseUrl}${primaryImage.medium_url || primaryImage.url}`;
    }

    // Fallback al sistema antiguo (soporta ambas nomenclaturas)
    return product.imagen_url || product.image_url;
  };

  // Auto-play del carousel
  useEffect(() => {
    if (!isAutoPlaying || totalSlides <= 1) return;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) =>
        prevIndex === totalSlides - 1 ? 0 : prevIndex + 1
      );
    }, 5000); // Cambiar cada 5 segundos

    return () => clearInterval(interval);
  }, [isAutoPlaying, totalSlides]);

  const goToNext = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prevIndex) =>
      prevIndex === totalSlides - 1 ? 0 : prevIndex + 1
    );
  };

  const goToPrev = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? totalSlides - 1 : prevIndex - 1
    );
  };

  const handleProductClick = (product) => {
    if (onProductClick) {
      onProductClick(product);
    }
  };

  if (!products || products.length === 0) {
    return (
      <div className="product-carousel-empty">
        <p>No hay productos disponibles en este momento</p>
      </div>
    );
  }

  // Obtener productos visibles en la página actual
  const startIndex = currentIndex * productsPerView;
  const visibleProducts = products.slice(startIndex, startIndex + productsPerView);

  return (
    <div className="product-carousel-horizontal">
      {totalSlides > 1 && (
        <button
          className="product-carousel-horizontal__nav product-carousel-horizontal__nav--prev"
          onClick={goToPrev}
          aria-label="Productos anteriores"
        >
          <FiChevronLeft />
        </button>
      )}

      <div className="product-carousel-horizontal__container" ref={scrollContainerRef}>
        <div className="product-carousel-horizontal__track">
          {visibleProducts.map((product, index) => (
            <div
              key={product.id || startIndex + index}
              className="product-carousel-horizontal__item"
              onClick={() => handleProductClick(product)}
              role="button"
              tabIndex={0}
              onKeyPress={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleProductClick(product);
                }
              }}
            >
              {getProductImageUrl(product) ? (
                <img
                  src={getProductImageUrl(product)}
                  alt={product.nombre || product.name}
                  className="product-carousel-horizontal__image"
                />
              ) : (
                <ProductPlaceholder className="product-carousel-horizontal__placeholder" />
              )}

              {product.es_nuevo && (
                <span className="product-carousel-horizontal__badge product-carousel-horizontal__badge--new">
                  NUEVO
                </span>
              )}

              {product.descuento > 0 && (
                <span className="product-carousel-horizontal__badge product-carousel-horizontal__badge--discount">
                  -{product.descuento}%
                </span>
              )}
            </div>
          ))}
        </div>
      </div>

      {totalSlides > 1 && (
        <button
          className="product-carousel-horizontal__nav product-carousel-horizontal__nav--next"
          onClick={goToNext}
          aria-label="Productos siguientes"
        >
          <FiChevronRight />
        </button>
      )}

      {totalSlides > 1 && (
        <div className="product-carousel-horizontal__indicators">
          {Array.from({ length: totalSlides }).map((_, index) => (
            <button
              key={index}
              className={`product-carousel-horizontal__indicator ${
                index === currentIndex ? 'product-carousel-horizontal__indicator--active' : ''
              }`}
              onClick={() => {
                setIsAutoPlaying(false);
                setCurrentIndex(index);
              }}
              aria-label={`Ir a página ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductCarousel;
